import React, { useState, useEffect, useCallback } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Box,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';

interface Company {
    id: number;
    name: string;
    slug?: string;
}

interface CompanySelectorProps {
    value: number | null;
    onChange: (companyId: number | null) => void;
    disabled?: boolean;
}

const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

export const CompanySelector: React.FC<CompanySelectorProps> = ({
    value,
    onChange,
    disabled = false,
}) => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);

    const getToken = () => localStorage.getItem('token');

    const loadCompanies = useCallback(async () => {
        const token = getToken();
        if (!token) {
            console.error('CompanySelector: No auth token available');
            setLoading(false);
            return;
        }

        try {
            const url = `${API_URL}/analytics/companies`;
            console.log('CompanySelector: Fetching companies from', url);

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                },
            });

            console.log('CompanySelector: Response status', response.status);

            if (response.ok) {
                const data = await response.json();
                console.log('CompanySelector: Received data', data);
                setCompanies(data.companies || []);
            } else {
                const errorText = await response.text();
                console.error('CompanySelector: Failed to load companies', response.status, errorText);
            }
        } catch (err) {
            console.error('CompanySelector: Error loading companies:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadCompanies();
    }, [loadCompanies]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    return (
        <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BusinessIcon fontSize="small" />
                    Mandant
                </Box>
            </InputLabel>
            <Select
                value={value === null ? '' : value}
                label="Mandant"
                onChange={(e) => {
                    const newValue = e.target.value as string | number;
                    onChange(newValue === '' ? null : Number(newValue));
                }}
            >
                <MenuItem value="">
                    <em>Alle Mandanten</em>
                </MenuItem>
                {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                        {company.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default CompanySelector;
